import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTimes, 
  faGlobe, 
  faSpinner
} from '@fortawesome/free-solid-svg-icons';
import { 
  faInstagram, 
  faFacebook, 
  faTwitter, 
  faYoutube, 
  faLinkedin 
} from '@fortawesome/free-brands-svg-icons';
import { doc, getDoc } from 'firebase/firestore';
import { getFirestoreInstance } from '../../utils/firebaseHelper';
import styles from './BrandsModal.module.css';
import defaultLogo from '../../assets/Images/defaultUserProfilePicture.png';

const BrandsModal = ({ brand, onClose }) => {
  const [brandDetails, setBrandDetails] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBrandDetails = async () => {
      try {
        setLoading(true);
        
        // If the brand already has all details needed
        if (brand.metadata && brand.socialLinks) {
          setBrandDetails(brand);
          setLoading(false);
          return;
        }
        
        // Otherwise fetch from Firestore
        const db = getFirestoreInstance();
        
        // Try to find the brand in both chunks
        let brandDoc = null;
        const chunks = ['brands_chunk1', 'brands_chunk2'];
        
        for (const chunkId of chunks) {
          const docRef = doc(db, 'musicalBrands', chunkId);
          const docSnap = await getDoc(docRef);
          
          if (docSnap.exists()) {
            const data = docSnap.data();
            const foundBrand = data.data?.find(b => 
              b.brandName.toLowerCase() === brand.brandName.toLowerCase()
            );
            
            if (foundBrand) {
              brandDoc = foundBrand;
              break;
            }
          }
        }
        
        if (brandDoc) {
          setBrandDetails(brandDoc);
        } else {
          // If not found in chunks, use the original brand data
          setBrandDetails(brand);
        }
      } catch (error) {
        console.error('Error fetching brand details:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchBrandDetails();
  }, [brand]);

  const getBrandImage = () => {
    if (!brandDetails) return defaultLogo;
    
    // Check if there's an OG image
    if (brandDetails.og_metadata?.image) {
      return brandDetails.og_metadata.image;
    }
    
    // If not, check for logo
    if (brandDetails.logo) {
      return brandDetails.logo;
    }
    
    // Default to favicon if available
    if (brandDetails.favicon?.url) {
      return brandDetails.favicon.url;
    }
    
    return defaultLogo;
  };

  const getWebsiteUrl = () => {
    if (!brandDetails) return '#';
    
    if (brandDetails.socialLinks?.website) {
      return brandDetails.socialLinks.website;
    }
    
    if (brandDetails.metadata?.canonical_url) {
      return brandDetails.metadata.canonical_url;
    }
    
    if (brandDetails.og_metadata?.url) {
      return brandDetails.og_metadata.url;
    }
    
    return '#';
  };

  const getDescription = () => {
    if (!brandDetails) return '';
    
    if (brandDetails.metadata?.description) {
      return brandDetails.metadata.description;
    }
    
    if (brandDetails.og_metadata?.description) {
      return brandDetails.og_metadata.description;
    }
    
    return '';
  };

  const renderSocialIcons = () => {
    if (!brandDetails || !brandDetails.socialLinks) return null;
    
    const socialLinks = brandDetails.socialLinks;
    const icons = [];
    
    if (socialLinks.website) {
      icons.push(
        <a 
          key="website" 
          href={socialLinks.website} 
          target="_blank" 
          rel="noopener noreferrer"
          className={styles.socialLink}
        >
          <FontAwesomeIcon icon={faGlobe} className={styles.socialIcon} />
        </a>
      );
    }
    
    if (socialLinks.instagram) {
      icons.push(
        <a 
          key="instagram" 
          href={socialLinks.instagram} 
          target="_blank" 
          rel="noopener noreferrer"
          className={styles.socialLink}
        >
          <FontAwesomeIcon icon={faInstagram} className={styles.socialIcon} />
        </a>
      );
    }
    
    if (socialLinks.facebook) {
      icons.push(
        <a 
          key="facebook" 
          href={socialLinks.facebook} 
          target="_blank" 
          rel="noopener noreferrer"
          className={styles.socialLink}
        >
          <FontAwesomeIcon icon={faFacebook} className={styles.socialIcon} />
        </a>
      );
    }
    
    if (socialLinks.twitter) {
      icons.push(
        <a 
          key="twitter" 
          href={socialLinks.twitter} 
          target="_blank" 
          rel="noopener noreferrer"
          className={styles.socialLink}
        >
          <FontAwesomeIcon icon={faTwitter} className={styles.socialIcon} />
        </a>
      );
    }
    
    if (socialLinks.youtube) {
      icons.push(
        <a 
          key="youtube" 
          href={socialLinks.youtube} 
          target="_blank" 
          rel="noopener noreferrer"
          className={styles.socialLink}
        >
          <FontAwesomeIcon icon={faYoutube} className={styles.socialIcon} />
        </a>
      );
    }
    
    if (socialLinks.linkedin) {
      icons.push(
        <a 
          key="linkedin" 
          href={socialLinks.linkedin} 
          target="_blank" 
          rel="noopener noreferrer"
          className={styles.socialLink}
        >
          <FontAwesomeIcon icon={faLinkedin} className={styles.socialIcon} />
        </a>
      );
    }
    
    return (
      <div className={styles.socialIcons}>
        {icons}
      </div>
    );
  };

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
        <button className={styles.closeButton} onClick={onClose}>
          <FontAwesomeIcon icon={faTimes} />
        </button>
        
        {loading ? (
          <div className={styles.loadingContainer}>
            <FontAwesomeIcon icon={faSpinner} spin className={styles.loadingIcon} />
            <p>Loading brand details...</p>
          </div>
        ) : (
          <div className={styles.brandContainer}>
            <div className={styles.brandHeader}>
              <div className={styles.brandImageContainer}>
              <img 
                src={getBrandImage()} 
                alt={brandDetails?.brandName || 'Brand'} 
                className={styles.brandLogo}
                onError={(e) => { e.target.onerror = null; e.target.src = defaultLogo; }}
              />
            </div>
              <h2 className={styles.brandName}>{brandDetails?.brandName}</h2>
            </div>
            
            {getDescription() && (
              <div className={styles.brandDescription}>
                <p>{getDescription()}</p>
              </div>
            )}
            
            {renderSocialIcons()}
            
            <div className={styles.visitWebsiteContainer}>
              <a 
                href={getWebsiteUrl()} 
                target="_blank" 
                rel="noopener noreferrer"
                className={styles.visitWebsiteButton}
              >
                <FontAwesomeIcon icon={faGlobe} className={styles.websiteIcon} />
                Visit Website
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BrandsModal;